import React from "react";
import {
  Puzzle,
  Target,
  Timer,
  Radio,
  Activity,
  ServerCog,
  Github
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n";

const featureIcons = [Puzzle, Target, Timer, Radio, Activity, ServerCog];
const featureColors = [
  "text-green-400",
  "text-teal-400",
  "text-emerald-400",
  "text-cyan-400",
  "text-green-300",
  "text-teal-300"
];

const OpenClawSection = () => {
  const { t } = useLanguage();
  const features = t('openclaw.features');

  return (
    <section id="openclaw" className="py-20 sm:py-28 px-4 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/5 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-16 space-y-4">
          <span className="text-green-400 text-sm font-medium uppercase tracking-widest">
            {t('openclaw.label')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">
            {t('openclaw.title')}{" "}
            <span className="bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
              {t('openclaw.titleHighlight')}
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('openclaw.description')}
          </p>
        </div>

        {/* Features grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = featureIcons[index];
            const color = featureColors[index];
            return (
              <div
                key={index}
                className="group relative glass-card rounded-2xl p-6 sm:p-8 hover:border-green-400/30 transition-all duration-300"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <Icon className={`w-6 h-6 ${color}`} />
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover glow */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            );
          })}
        </div>

        {/* GitHub CTA */}
        <div className="mt-12 text-center">
          <Button variant="hero-outline" size="lg" asChild>
            <a
              href="https://github.com/ajamaica/lumiere"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <Github className="w-5 h-5" />
              {t('openclaw.cta')}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default OpenClawSection;
